import styles from './encounter-diagnosis-section.module.css';
import { useTranslation } from '@/contexts/i18n-context';
import { EncounterResolution, GetTreatmentPlanResponse } from '@/api/api.schemas';
import { Section } from '../containers/section';
import clsx from 'clsx';
import {
  CompleteMessageFromProviderSection,
  TriageOutMessageFromProviderSection,
} from './message-from-provider-section';
import visitCanceledIcon from '@/img/visit-canceled.svg';
import visitInProgressIcon from '@/img/visit-in-progress.svg';

interface EncounterDiagnosisSectionProps {
  treatmentPlan: GetTreatmentPlanResponse;
  workflowId?: number;
}

const translationBase = 'visit.treatmentPlan.diagnosisSection';

export function EncounterDiagnosisSection({ treatmentPlan }: EncounterDiagnosisSectionProps) {
  const isPatientCancelled =
    treatmentPlan.encounterResolution === EncounterResolution.PatientCancelled;

  const isTriagedOut = treatmentPlan.encounterResolution === EncounterResolution.TriageOut;

  const isInProgress = treatmentPlan.encounterResolution === EncounterResolution.ProviderInProgress;

  const isLegacyVisitInAwaiting =
    treatmentPlan.encounterResolution === EncounterResolution.AwaitingLegacy;

  if (isPatientCancelled) {
    return <CanceledDiagnosisSection />;
  }

  if (isTriagedOut) {
    return <TriagedOutDiagnosisSection treatmentPlan={treatmentPlan} />;
  }

  if (isInProgress) {
    return <InProgressDiagnosisSection />;
  }

  if (isLegacyVisitInAwaiting) {
    return <InAwaitingLegacyVisitSection />;
  }

  return <StandardDiagnosisSection treatmentPlan={treatmentPlan} />;
}

function StandardDiagnosisSection({ treatmentPlan }: EncounterDiagnosisSectionProps) {
  const { t } = useTranslation();

  return (
    <Section title={t(`${translationBase}.title`)}>
      {treatmentPlan.diagnosisName && (
        <>
          <p className="h4">{treatmentPlan.diagnosisName}</p>
          {treatmentPlan.diagnosisIcd10Code && (
            <p className={clsx('body-1', styles.icdCode)}>
              <span>{t(`${translationBase}.icd10Code`)}</span>
              {treatmentPlan.diagnosisIcd10Code}
            </p>
          )}

          {treatmentPlan.aboutYourDiagnosis?.whatIs && (
            <div className={styles.whatIsContainer}>
              <p className="semibold">
                {t(`${translationBase}.whatIs`, { diagnosisName: treatmentPlan.diagnosisName })}
              </p>
              <p>{treatmentPlan.aboutYourDiagnosis.whatIs}</p>
            </div>
          )}
        </>
      )}

      {treatmentPlan.assignedProvider &&
        (treatmentPlan.messageFromProvider || treatmentPlan.referralTextHtml) && (
          <CompleteMessageFromProviderSection
            encounterId={treatmentPlan.encounterId}
            messageFromProvider={treatmentPlan.messageFromProvider}
            referralTextHtml={treatmentPlan.referralTextHtml}
            assignedProvider={treatmentPlan.assignedProvider}
            approvedProvider={treatmentPlan.approvedProvider}
            followUpVisitDate={treatmentPlan.followUpVisitDate}
          />
        )}
    </Section>
  );
}

function CanceledDiagnosisSection() {
  const { t } = useTranslation();

  return (
    <Section className={styles.canceledDiagnosisSection}>
      <img src={visitCanceledIcon} alt="" />
      <p className={clsx('body-1', 'bold', styles.title)}>
        {t(`${translationBase}.canceledTitle`)}
      </p>
      <p className={styles.description}>{t(`${translationBase}.canceledDescription`)}</p>
    </Section>
  );
}

function TriagedOutDiagnosisSection({ treatmentPlan }: EncounterDiagnosisSectionProps) {
  if (!treatmentPlan.assignedProvider) {
    return null;
  }

  return (
    <Section>
      <TriageOutMessageFromProviderSection
        triageOutMessage={treatmentPlan.triageOutMessage}
        encounterId={treatmentPlan.encounterId}
        hasReferralSpecialist={treatmentPlan.hasReferralSpecialist}
        messageFromProvider={treatmentPlan.messageFromProvider}
        assignedProvider={treatmentPlan.assignedProvider}
        approvedProvider={treatmentPlan.approvedProvider}
      />
    </Section>
  );
}

function InProgressDiagnosisSection() {
  const { t } = useTranslation();

  return (
    <Section className={styles.canceledDiagnosisSection}>
      <img src={visitInProgressIcon} alt="" />
      <p className={clsx('body-1', 'bold', styles.title)}>
        {t(`${translationBase}.inProgressTitle`)}
      </p>
      <p className={styles.description}>{t(`${translationBase}.inProgressDescription`)}</p>
    </Section>
  );
}

function InAwaitingLegacyVisitSection() {
  const { t } = useTranslation();

  return (
    <Section className={styles.canceledDiagnosisSection}>
      <img src={visitInProgressIcon} alt="" />
      <p className={clsx('body-1', 'bold', styles.title)}>
        {t(`${translationBase}.inProgressLegacyTitle`)}
      </p>
      <p className={styles.description}>{t(`${translationBase}.inProgressLegacyDescription`)}</p>
    </Section>
  );
}
